import { MessageSquare, Star } from 'lucide-react';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { reputationLabel, type MlReputation } from '@/hooks/useMlMetrics';

// ─── Funil: visitas → pedidos → pagos ─────────────────────────────────────────

interface FunnelProps {
  visits?: number | null;
  orders?: number;
  paid?: number;
}

export function ConversionFunnel({ visits, orders = 0, paid = 0 }: FunnelProps) {
  const hasData = visits != null && visits > 0;
  const steps = [
    { label: 'Visitas (30d)', value: visits ?? 0 },
    { label: 'Pedidos',       value: orders },
    { label: 'Pagos',         value: paid },
  ];
  const max = Math.max(...steps.map((s) => s.value), 1);

  return (
    <div className="card p-5 h-full">
      <SectionHeader
        title="Funil de conversão"
        subtitle="Visitas → pedidos → pagos (últimos 30 dias)"
      />
      {!hasData ? (
        <EmptyState
          icon={Star}
          title="Sem dados de conversão"
          description="As visitas dos anúncios sincronizam automaticamente com o Mercado Livre."
        />
      ) : (
        <div className="space-y-3 mt-2">
          {steps.map((s) => (
            <div key={s.label}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-ink-muted">{s.label}</span>
                <span className="font-semibold text-ink tabular-nums">{s.value.toLocaleString('pt-BR')}</span>
              </div>
              <div className="h-2.5 rounded-full bg-surface-subtle overflow-hidden">
                <div
                  className="h-full rounded-full bg-brand-700"
                  style={{ width: `${Math.max(2, (s.value / max) * 100)}%` }}
                />
              </div>
            </div>
          ))}
          <p className="text-xs text-ink-muted pt-1">
            Conversão visita→pedido:{' '}
            <span className="font-semibold text-ink">
              {visits && visits > 0 ? `${((orders / visits) * 100).toFixed(2)}%` : '—'}
            </span>
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Atendimento e reputação ──────────────────────────────────────────────────

interface ServiceProps {
  questionsOpen?: number | null;
  reputation?: MlReputation | null;
}

export function CustomerService({ questionsOpen, reputation }: ServiceProps) {
  const rep = reputationLabel(reputation);
  const ratings = reputation?.transactions?.ratings;
  const total = reputation?.transactions?.total;
  const hasData = questionsOpen != null || reputation != null;

  return (
    <div className="card p-5 h-full">
      <SectionHeader
        title="Atendimento e reputação"
        subtitle="Perguntas abertas e reputação no Mercado Livre"
      />
      {!hasData ? (
        <EmptyState
          icon={MessageSquare}
          title="Sem dados de atendimento"
          description="Sincroniza automaticamente com o Mercado Livre."
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div className="rounded-xl bg-surface-subtle p-4 text-center">
            <p className="text-2xl font-bold text-ink">{questionsOpen ?? 0}</p>
            <p className="text-xs text-ink-muted mt-1">Perguntas sem resposta</p>
          </div>
          <div className="rounded-xl bg-surface-subtle p-4 text-center">
            <p className="text-2xl font-bold text-ink">{rep.label}</p>
            <p className="text-xs text-ink-muted mt-1">Reputação{total != null ? ` · ${total} vendas` : ''}</p>
          </div>
          {ratings && (
            <div className="col-span-2 text-xs text-ink-muted space-y-1">
              <div className="flex justify-between">
                <span>Avaliações positivas</span>
                <span className="text-positive font-semibold">{Math.round((ratings.positive ?? 0) * 100)}%</span>
              </div>
              <div className="flex justify-between">
                <span>Neutras</span>
                <span className="font-semibold">{Math.round((ratings.neutral ?? 0) * 100)}%</span>
              </div>
              <div className="flex justify-between">
                <span>Negativas</span>
                <span className="text-negative font-semibold">{Math.round((ratings.negative ?? 0) * 100)}%</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
